import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Briefcase, Clock, AlertCircle, TrendingUp, 
  Calendar, CheckCircle, ArrowRight, BarChart3, Filter 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePlan } from '../context/PlanContext';
import TeamWorkloadView from '../components/team/TeamWorkloadView';
import { teamsApi } from '../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Team Dashboard - Phase 3: Team Collaboration
 * 
 * Provides team-level overview for TEAM and ENTERPRISE plans
 */
const TeamDashboard = () => {
  const navigate = useNavigate();
  const { plan, features } = usePlan();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [cases, setCases] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview'); // overview, workload

  useEffect(() => {
    // Wait for plan to load
    if (!plan) {
      console.log('⏳ Waiting for plan data...');
      return;
    }

    console.log('📋 Current plan:', plan);
    console.log('📋 Plan type:', plan.plan_type);
    
    // Check if user has access to team dashboard
    const planType = plan.plan_type || plan;
    if (planType !== 'TEAM' && planType !== 'ENTERPRISE') {
      console.log('❌ Access denied. Required: TEAM or ENTERPRISE, Got:', planType);
      toast.error(`Team Dashboard krever TEAM eller ENTERPRISE plan. Din plan: ${planType}`);
      navigate('/lexy/dashboard');
      return;
    }

    console.log('✅ Access granted, fetching team dashboard data...');
    fetchDashboardData();
  }, [plan, navigate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      console.log('🔍 Fetching team dashboard data...');
      
      // Fetch dashboard statistics
      const dashboardRes = await axios.get(`${API}/teams/dashboard/`, { headers });
      console.log('✅ Dashboard stats:', dashboardRes.data);
      setDashboardData(dashboardRes.data);

      // Fetch team cases
      const casesRes = await axios.get(`${API}/teams/dashboard/cases?limit=20`, { headers });
      console.log('✅ Team cases:', casesRes.data);
      setCases(casesRes.data.cases);

      // Fetch upcoming deadlines
      const deadlinesRes = await axios.get(`${API}/teams/dashboard/deadlines?days=30`, { headers });
      console.log('✅ Deadlines:', deadlinesRes.data);
      setDeadlines(deadlinesRes.data.deadlines);

    } catch (error) {
      console.error('❌ Failed to fetch team dashboard:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      
      // Show specific error message
      const errorMsg = error.response?.data?.detail || 'Kunne ikke laste team dashboard';
      toast.error(errorMsg);
      
      // If authentication error, don't block the UI - show error state
      if (error.response?.status === 401 || error.response?.status === 403) {
        setDashboardData({
          total_cases: 0,
          active_cases: 0,
          team_members: 0,
          upcoming_deadlines: 0
        });
        setCases([]);
        setDeadlines([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'ASSIGNED': 'bg-blue-100 text-blue-700',
      'IN_PROGRESS': 'bg-yellow-100 text-yellow-700',
      'AWAITING_LAWYER_REVIEW': 'bg-slate-100 text-slate-800',
      'COMPLETED': 'bg-green-100 text-green-700',
      'CLOSED': 'bg-gray-100 text-gray-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'ASSIGNED': 'Tildelt',
      'IN_PROGRESS': 'Pågår',
      'AWAITING_LAWYER_REVIEW': 'Venter på gjennomgang',
      'COMPLETED': 'Fullført',
      'CLOSED': 'Lukket',
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filteredCases = statusFilter === 'all' 
    ? cases 
    : cases.filter(c => c.status === statusFilter);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laster team dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Kunne ikke laste dashboard data</p>
        </div>
      </div>
    );
  }

  const { statistics, workload, tenant } = dashboardData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Team Dashboard</h1>
              <p className="text-gray-600">
                {tenant.name} · <span className="font-semibold text-slate-900">{tenant.plan} Plan</span>
              </p>
            </div>
            <button
              onClick={() => navigate('/lexy/dashboard')}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Tilbake til Min Dashboard
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'overview'
                  ? 'border-slate-600 text-slate-900'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Oversikt
            </button>
            <button
              onClick={() => setActiveTab('workload')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'workload'
                  ? 'border-slate-600 text-slate-900'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <TrendingUp className="w-4 h-4 inline mr-2" />
              Ressurser & Kapasitet
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'workload' && <TeamWorkloadView />}
        
        {activeTab === 'overview' && (
          <div>
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Cases */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-slate-900" />
              </div>
              <span className="text-2xl font-bold text-slate-900">{statistics.total_cases}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Totalt saker</h3>
          </div>

          {/* Active Cases */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{statistics.active_cases}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Aktive saker</h3>
          </div>

          {/* Team Members */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{statistics.team_members}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Teammedlemmer</h3>
          </div>

          {/* Upcoming Deadlines */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{statistics.upcoming_deadlines}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-600">Kommende frister (30d)</h3>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Team Cases */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-slate-900" />
                    Team Saker
                  </h2>
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                    >
                      <option value="all">Alle ({cases.length})</option>
                      <option value="ASSIGNED">Tildelt</option>
                      <option value="IN_PROGRESS">Pågår</option>
                      <option value="AWAITING_LAWYER_REVIEW">Venter</option>
                      <option value="COMPLETED">Fullført</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="overflow-y-auto max-h-[600px]">
                {filteredCases.length === 0 ? (
                  <div className="p-12 text-center">
                    <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">Ingen saker funnet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredCases.map((caseItem) => (
                      <div
                        key={caseItem.id}
                        onClick={() => navigate(`/lexy/case/${caseItem.id}`)}
                        className="p-4 hover:bg-[#F4F2EE] cursor-pointer transition-colors group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-slate-900 transition-colors">
                              {caseItem.topic || caseItem.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-2 line-clamp-1">
                              {caseItem.description || caseItem.intake_summary}
                            </p>
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(caseItem.status)}`}>
                                {getStatusLabel(caseItem.status)}
                              </span>
                              {caseItem.member_count > 0 && (
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {caseItem.member_count} medlem{caseItem.member_count > 1 ? 'mer' : ''}
                                </span>
                              )}
                              <span className="text-xs text-gray-500">
                                {formatDate(caseItem.updated_at)}
                              </span>
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-slate-900 transition-colors ml-4" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Workload */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                  Workload
                </h2>
              </div>
              <div className="p-4 space-y-3">
                {workload && workload.length > 0 ? (
                  workload.map((lawyer) => (
                    <div key={lawyer.user_id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{lawyer.user_name}</p>
                          <p className="text-xs text-gray-500">{lawyer.user_email}</p>
                        </div>
                        <span className="text-lg font-bold text-slate-900">{lawyer.total_cases}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          {lawyer.owned_cases} eier
                        </span>
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">
                          {lawyer.member_cases} medlem
                        </span>
                      </div>
                      {lawyer.total_cases > 0 && (
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full"
                            style={{ width: `${Math.min((lawyer.total_cases / 10) * 100, 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">Ingen workload data</p>
                )}
              </div>
            </div>

            {/* Upcoming Deadlines */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-amber-50">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-600" />
                  Kommende Frister
                </h2>
              </div>
              <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
                {deadlines && deadlines.length > 0 ? (
                  deadlines.slice(0, 10).map((deadline, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-[#F4F2EE] rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 mb-1">{deadline.title}</p>
                        <p className="text-xs text-gray-600 mb-1 line-clamp-1">{deadline.case_topic}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-orange-600 font-medium">
                            {formatDate(deadline.deadline)}
                          </span>
                          {deadline.assigned_to && (
                            <span className="text-xs text-gray-500">· {deadline.assigned_to}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Ingen kommende frister</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default TeamDashboard;
